"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import {
  type ComponentPropsWithoutRef,
  createContext,
  type FC,
  memo,
  useContext,
  useRef,
  useState,
} from "react";
import ReactMarkdown from "react-markdown";
import { CheckIcon, CopyIcon, DownloadIcon } from "lucide-react";

import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import { cn } from "@/lib/utils";
import {
  stripFollowups,
  stripHtmlBreaks,
} from "@/components/assistant-ui/followup-markers";

function prepareChatMarkdown(text: string): string {
  return stripHtmlBreaks(stripFollowups(text));
}

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
      preprocess={prepareChatMarkdown}
      defer
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const FencedPreContext = createContext(false);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div
      className="aui-code-header-root border-border bg-transparent mt-3 flex items-center justify-between gap-2 rounded-t-xl border border-b-0 px-3.5 py-1.5 text-xs"
      dir="ltr"
    >
      <span className="aui-code-header-language text-muted-foreground font-medium lowercase">
        {language || 'code'}
      </span>
      <TooltipIconButton tooltip="کپی" onClick={onCopy}>
        {!isCopied && (
          <CopyIcon className="animate-in zoom-in-75 fade-in duration-150" />
        )}
        {isCopied && (
          <CheckIcon className="animate-in zoom-in-50 fade-in duration-200 ease-out" />
        )}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value || typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(value).then(
      () => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), copiedDuration);
      },
      () => {},
    );
  };

  return { isCopied, copyToClipboard };
};

function tableCellText(cell: HTMLTableCellElement): string {
  return (cell.textContent ?? "").replace(/\s+/g, " ").trim();
}

function readTableRows(table: HTMLTableElement): string[][] {
  return Array.from(table.rows)
    .map(row => Array.from(row.cells).map(tableCellText))
    .filter(row => row.some(cell => cell.length > 0));
}

function escapeCsvCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function rowsToTsv(rows: string[][]): string {
  return rows.map(row => row.join("\t")).join("\n");
}

function rowsToCsv(rows: string[][]): string {
  return rows.map(row => row.map(escapeCsvCell).join(",")).join("\n");
}

function downloadCsv(csv: string) {
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "table.csv";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

const MarkdownTable: FC<
  ComponentPropsWithoutRef<"table"> & { node?: unknown }
> = ({ className, node: _node, ...props }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  const onCopy = () => {
    const table = tableRef.current;
    if (!table || isCopied) return;
    const rows = readTableRows(table);
    if (!rows.length) return;
    copyToClipboard(rowsToTsv(rows));
  };

  const onDownloadCsv = () => {
    const table = tableRef.current;
    if (!table) return;
    const rows = readTableRows(table);
    if (!rows.length) return;
    downloadCsv(rowsToCsv(rows));
  };

  return (
    <div className="aui-md-table-block my-3">
      <div
        className="aui-md-table-toolbar mb-1 flex items-center justify-end gap-0.5"
        dir="rtl"
      >
        <TooltipIconButton
          tooltip="کپی جدول"
          onClick={onCopy}
          className="size-7"
        >
          {!isCopied && (
            <CopyIcon className="size-3.5 animate-in zoom-in-75 fade-in duration-150" />
          )}
          {isCopied && (
            <CheckIcon className="size-3.5 animate-in zoom-in-50 fade-in duration-200 ease-out" />
          )}
        </TooltipIconButton>
        <TooltipIconButton
          tooltip="خروجی CSV"
          onClick={onDownloadCsv}
          className="size-7"
        >
          <DownloadIcon className="size-3.5" />
        </TooltipIconButton>
      </div>
      <div className="aui-md-table-scroll overflow-x-auto">
        <table
          className={cn(
            "aui-md-table w-full border-separate border-spacing-0",
            className,
          )}
          {...props}
          ref={tableRef}
        />
      </div>
    </div>
  );
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mt-5 mb-2 scroll-m-20 text-xl font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-5 mb-2 scroll-m-20 text-lg font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-4 mb-1.5 scroll-m-20 text-base font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-3.5 mb-1 scroll-m-20 text-base font-medium first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mt-3 mb-1 text-sm font-semibold first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mt-3 mb-1 text-sm font-medium first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p mt-3 mb-0 leading-relaxed first:mt-0",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn(
        "aui-md-a text-primary hover:text-primary/80 underline underline-offset-2",
        className,
      )}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        "aui-md-blockquote border-muted-foreground/30 text-muted-foreground my-3 border-s-2 ps-4",
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "aui-md-ul marker:text-muted-foreground my-3 ms-5 list-disc [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "aui-md-ol marker:text-muted-foreground my-3 ms-5 list-decimal [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr
      className={cn("aui-md-hr border-muted-foreground/20 my-3", className)}
      {...props}
    />
  ),
  table: MarkdownTable,
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-3 py-1.5 text-start font-medium first:rounded-ss-lg last:rounded-se-lg [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-muted-foreground/20 border-s border-b px-3 py-1.5 text-start last:border-e [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-es-lg [&:last-child>td:last-child]:rounded-ee-lg",
        className,
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn("aui-md-li leading-relaxed", className)} {...props} />
  ),
  strong: ({ className, ...props }) => (
    <strong
      className={cn("aui-md-strong font-semibold", className)}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <FencedPreContext.Provider value={true}>
      <pre
        dir="ltr"
        className={cn(
          "aui-md-pre border-border bg-transparent overflow-x-auto rounded-t-none rounded-b-xl border border-t-0 p-3.5 text-start font-mono text-[13px] leading-relaxed text-foreground",
          className,
        )}
        {...props}
      />
    </FencedPreContext.Provider>
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock() || useContext(FencedPreContext);
    return (
      <code
        dir={isCodeBlock ? 'ltr' : undefined}
        className={cn(
          isCodeBlock
            ? 'aui-md-code-block block w-full text-start whitespace-pre font-mono'
            : 'aui-md-inline-code bg-muted text-foreground rounded-md px-1.5 py-0.5 font-mono text-[0.85em]',
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});

/** Same chat markdown (headings, lists, tables) for text that is not a message part. */
export const ChatMarkdown: FC<{ text: string }> = ({ text }) => {
  return (
    <div className="aui-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={defaultComponents}
      >
        {prepareChatMarkdown(text)}
      </ReactMarkdown>
    </div>
  );
};
